import React, { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useList, useDeleteOne, useBulkDelete } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader, Loading, EmptyState, StatusBadge, fmt } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

export default function Packing() {
  const { canWrite, user } = useAuth();
  const qc = useQueryClient();
  const delOne = useDeleteOne((id) => `/packing/${id}`, ["packing"]);
  const clearAll = useBulkDelete((r) => `/packing/${r.id}`, ["packing"]);
  const { data: rows, isLoading } = useList("packing", "/packing");
  const { data: orders = [] } = useList("orders", "/orders");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ order_ref: "", packed_quantity: "", carton_number: "", pieces_per_carton: "", cartons: "", packing_staff: "", packing_date: "", status: "Packed" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.order_ref) return toast.error("Select order");
    const o = orders.find((x) => x.id === f.order_ref);
    try { await api.post("/packing", { ...f, order_id: o?.order_id, product_name: o?.product_name, packed_quantity: Number(f.packed_quantity) || 0, pieces_per_carton: Number(f.pieces_per_carton) || 0, cartons: Number(f.cartons) || 0 }); toast.success("Packing recorded"); qc.invalidateQueries(); setOpen(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (isLoading) return <Loading />;
  return (
    <div>
      <PageHeader title="Packing" subtitle="Carton packing records" testid="packing-header">
        {canWrite("packing") && <Button onClick={() => setOpen(true)} className="gap-2" data-testid="new-packing-btn"><Plus className="w-4 h-4" /> New Packing</Button>}
      </PageHeader>
      <Card className="overflow-hidden">
        {!rows?.length ? <EmptyState title="No packing records" /> : (
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow><TableHead>Order</TableHead><TableHead>Product</TableHead><TableHead className="text-right">Packed</TableHead><TableHead>Carton#</TableHead><TableHead className="text-right">Pcs/Carton</TableHead><TableHead className="text-right">Cartons</TableHead><TableHead>Staff</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>{rows.map((r) => (
              <TableRow key={r.id} data-testid={`packing-row-${r.id}`}>
                <TableCell className="font-num text-primary">{r.order_id}</TableCell><TableCell className="text-sm max-w-[160px] truncate">{r.product_name}</TableCell>
                <TableCell className="text-right font-num">{fmt(r.packed_quantity)}</TableCell><TableCell>{r.carton_number}</TableCell>
                <TableCell className="text-right font-num">{r.pieces_per_carton}</TableCell><TableCell className="text-right font-num">{r.cartons}</TableCell>
                <TableCell className="text-sm">{r.packing_staff}</TableCell><TableCell><StatusBadge status={r.status} /></TableCell>
                <TableCell>{canWrite("packing") && <Button size="icon" variant="ghost" onClick={() => delOne(r.id, "Delete this packing record?")} data-testid={`delete-packing-${r.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>}</TableCell>
              </TableRow>
            ))}</TableBody>
          </Table></div>
        )}
      </Card>
      <Dialog open={open} onOpenChange={setOpen}><DialogContent data-testid="packing-form">
        <DialogHeader><DialogTitle>Packing Record</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Order</Label>
            <Select value={f.order_ref} onValueChange={(v) => set("order_ref", v)}>
              <SelectTrigger data-testid="packing-order-select"><SelectValue placeholder="Order" /></SelectTrigger>
              <SelectContent>{orders.map((o) => <SelectItem key={o.id} value={o.id}>{o.order_id} — {o.product_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Packed Qty</Label><Input type="number" value={f.packed_quantity} onChange={(e) => set("packed_quantity", e.target.value)} /></div>
          <div><Label>Carton Number</Label><Input value={f.carton_number} onChange={(e) => set("carton_number", e.target.value)} /></div>
          <div><Label>Pieces / Carton</Label><Input type="number" value={f.pieces_per_carton} onChange={(e) => set("pieces_per_carton", e.target.value)} /></div>
          <div><Label># Cartons</Label><Input type="number" value={f.cartons} onChange={(e) => set("cartons", e.target.value)} /></div>
          <div><Label>Staff</Label><Input value={f.packing_staff} onChange={(e) => set("packing_staff", e.target.value)} /></div>
          <div><Label>Date</Label><Input type="date" value={f.packing_date} onChange={(e) => set("packing_date", e.target.value)} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} data-testid="packing-save-btn">Save</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}
